// Utility for lazy loading components with better error handling and preloading
import { ComponentType, lazy } from 'react';

interface LazyComponentOptions {
  fallback?: ComponentType;
  preload?: boolean;
}

export const createLazyComponent = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options: LazyComponentOptions = {}
) => {
  const LazyComponent = lazy(importFunc);
  
  // Preload the component if requested
  if (options.preload) {
    // Preload after a short delay to not block initial render
    setTimeout(() => {
      importFunc().catch(() => {
        // Silently handle preload errors
      });
    }, 100);
  }
  
  return LazyComponent;
};

// Preload critical components that are likely to be needed soon
export const preloadCriticalComponents = () => {
  // Preload Dashboard for authenticated users
  import('../pages/CompanyDashboard').catch(() => {});
  import('../pages/JobSeekerDashboard').catch(() => {});
  import('../pages/CompanyProfilePage').catch(() => {});
  import('../pages/JobSeekerProfilePage').catch(() => {});
  
  // Preload JobDetails as it's commonly accessed from JobListings
  import('../pages/JobDetails').catch(() => {});
  
  
};

// Preload components based on user interaction hints
export const preloadOnHover = (componentPath: string) => {
  return () => {
    switch (componentPath) {
      case 'dashboard':
        // No longer preloading a generic dashboard, handled by specific user types
        break;
      case 'profile':
        import('../pages/Profile').catch(() => {});
        break;
      case 'job-details':
        import('../pages/JobDetails').catch(() => {});
        break;
      default:
        break;
    }
  };
};